/**
 * 新規登録ページ - AIRGROW Material Interface
 * デザイン: DESIGN.md 参照
 * Step1: アカウント情報入力（氏名・メール・パスワード）
 * Step2: 利用形態選択（個人 or 組織）
 * Step3: メール確認コード
 * Step4: 登録完了
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signUp, confirmSignUp } from '../services/auth';
import Input from '../components/ui/Input';
import Alert from '../components/ui/Alert';
import AuthLayout, { AuthSubmitButton } from '../components/ui/AuthLayout';
import { C } from '../ColorTokens';

const STEPS = { ACCOUNT: 1, ORG_TYPE: 2, VERIFY: 3, COMPLETE: 4 };

// 左ペイン共通テキスト（全ステップ共通）
const LEFT_HEADING = <>JOIN<br />AIRGROW</>;
const LEFT_BODY = (
  <>
    ドローン運航管理の新しいスタンダード。<br />
    アカウントを作成して、スマートな<br />
    フライトプランニングを始めましょう。
  </>
);

// ---- エラー表示 ----
function ErrorBanner({ message }) {
  if (!message) return null;
  return <Alert variant="error">{message}</Alert>;
}

// ---- 戻るボタン ----
function BackButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 transition-colors"
      style={{
        fontFamily: 'Noto Sans JP', fontSize: '13px',
        color: C.onSurfaceVariant, background: 'none',
        border: 'none', cursor: 'pointer', padding: '4px 0',
      }}
      onMouseEnter={e => e.currentTarget.style.color = C.primary}
      onMouseLeave={e => e.currentTarget.style.color = C.onSurfaceVariant}
    >
      <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>arrow_back</span>
      戻る
    </button>
  );
}

// ---- HUD ステップインジケーター ----
function StepIndicator({ currentStep }) {
  const steps = ['ACCOUNT', 'ORG TYPE', 'VERIFY'];
  return (
    <div className="flex items-center" style={{ marginBottom: '32px' }}>
      {steps.map((label, i) => {
        const stepNum = i + 1;
        const isCompleted = currentStep > stepNum;
        const isActive = currentStep === stepNum;
        return (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center gap-1.5">
              <div style={{
                width: '28px', height: '28px', borderRadius: '9999px',
                border: isActive ? `2px solid ${C.primary}` : isCompleted ? 'none' : `2px solid ${C.outlineVariant}`,
                backgroundColor: isCompleted ? C.primary : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.3s',
              }}>
                {isCompleted ? (
                  <span className="material-symbols-outlined text-white" style={{ fontSize: '16px', fontVariationSettings: "'FILL' 1" }}>check</span>
                ) : (
                  <span style={{ fontFamily: 'Space Grotesk', fontSize: '12px', fontWeight: 700, color: isActive ? C.primary : C.outlineVariant }}>
                    {stepNum}
                  </span>
                )}
              </div>
              <span style={{
                fontFamily: 'Space Grotesk', fontSize: '10px', fontWeight: 500, letterSpacing: '0.5px',
                color: isActive ? C.primary : isCompleted ? C.primary : C.outlineVariant,
              }}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{
                flex: 1, height: '1px', margin: '0 8px', marginBottom: '18px',
                backgroundColor: currentStep > i + 1 ? C.primary : C.outlineVariant,
                transition: 'background-color 0.3s',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ---- 利用形態選択カード ----
function OrgTypeCard({ icon, title, description, selected, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '16px',
        padding: '16px 20px', borderRadius: '16px',
        border: `2px solid ${selected ? C.primary : C.outlineVariant}`,
        backgroundColor: selected ? 'rgba(0,80,142,0.05)' : 'transparent',
        cursor: 'pointer', transition: 'all 0.2s', backdropFilter: 'blur(4px)',
      }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.borderColor = C.outline; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.borderColor = C.outlineVariant; }}
    >
      <div style={{
        width: '44px', height: '44px', borderRadius: '12px',
        backgroundColor: selected ? C.primaryContainer : C.surfaceContainerHighest,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, transition: 'background-color 0.2s',
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: '22px', color: selected ? '#fff' : C.onSurfaceVariant, fontVariationSettings: "'FILL' 1" }}>
          {icon}
        </span>
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ fontFamily: 'Noto Sans JP', fontSize: '15px', fontWeight: 700, color: C.onSurface, marginBottom: '4px' }}>{title}</p>
        <p style={{ fontFamily: 'Noto Sans JP', fontSize: '12px', color: C.onSurfaceVariant, lineHeight: 1.6 }}>{description}</p>
      </div>
      <div style={{
        width: '20px', height: '20px', borderRadius: '9999px',
        border: `2px solid ${selected ? C.primary : C.outlineVariant}`,
        backgroundColor: selected ? C.primary : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, marginTop: '2px', transition: 'all 0.2s',
      }}>
        {selected && <div style={{ width: '8px', height: '8px', borderRadius: '9999px', backgroundColor: '#fff' }} />}
      </div>
    </div>
  );
}

// ================================================================
// メインコンポーネント
// ================================================================
export default function Register() {
  const navigate = useNavigate();
  const [step,         setStep]        = useState(STEPS.ACCOUNT);
  const [isLoading,    setIsLoading]   = useState(false);
  const [error,        setError]       = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    email: '', password: '', confirmPassword: '',
    name: '', orgType: '', orgName: '', verificationCode: '',
  });

  // ブラウザBACK制御
  React.useEffect(() => {
    if (step === STEPS.ORG_TYPE) {
      window.history.pushState({ registerStep: 'org_type' }, '');
      const handler = () => setStep(STEPS.ACCOUNT);
      window.addEventListener('popstate', handler);
      return () => window.removeEventListener('popstate', handler);
    }
    if (step === STEPS.VERIFY) {
      window.history.pushState({ registerStep: 'verify' }, '');
      const handler = () => window.history.pushState({ registerStep: 'verify' }, '');
      window.addEventListener('popstate', handler);
      return () => window.removeEventListener('popstate', handler);
    }
  }, [step]);

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  // パスワードポリシーチェック
  const passwordChecks = {
    length:    form.password.length >= 8,
    uppercase: /[A-Z]/.test(form.password),
    lowercase: /[a-z]/.test(form.password),
    number:    /[0-9]/.test(form.password),
    match:     form.password === form.confirmPassword && form.confirmPassword.length > 0,
  };
  const isPasswordValid     = Object.values(passwordChecks).every(Boolean);
  const isAccountFormValid  = form.email && form.name && isPasswordValid;

  const handleAccountNext = (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.name) { setError('すべての項目を入力してください'); return; }
    if (!isPasswordValid) { setError('パスワードの要件を満たしてください'); return; }
    setStep(STEPS.ORG_TYPE);
  };

  const handleOrgTypeNext = async (e) => {
    e.preventDefault();
    if (!form.orgType) { setError('利用形態を選択してください'); return; }
    if (form.orgType === 'organization' && !form.orgName.trim()) { setError('組織名を入力してください'); return; }
    setIsLoading(true);
    setError('');
    try {
      await signUp({ email: form.email, password: form.password, name: form.name, orgType: form.orgType });
      setStep(STEPS.VERIFY);
    } catch (err) {
      setError(err.code === 'UsernameExistsException' ? 'このメールアドレスはすでに登録されています' : err.message || '登録に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!form.verificationCode) { setError('確認コードを入力してください'); return; }
    setIsLoading(true);
    setError('');
    try {
      await confirmSignUp(form.email, form.verificationCode);
      sessionStorage.setItem('pendingRegistration', JSON.stringify({
        name: form.name, orgType: form.orgType,
        orgName: form.orgType === 'organization' ? form.orgName : form.name,
      }));
      setStep(STEPS.COMPLETE);
    } catch (err) {
      if (err.code === 'CodeMismatchException') setError('確認コードが正しくありません');
      else if (err.code === 'ExpiredCodeException') setError('確認コードの有効期限が切れています。再送信してください');
      else setError(err.message || '確認に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError('');
    try {
      const { resendConfirmationCode } = await import('../services/auth');
      await resendConfirmationCode(form.email);
      alert('確認コードを再送信しました');
    } catch {
      setError('再送信に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // ================================================================
  // Step4: 登録完了
  // ================================================================
  if (step === STEPS.COMPLETE) {
    return (
      <AuthLayout leftHeading={LEFT_HEADING} leftBody={LEFT_BODY}>
        <div className="flex flex-col items-center text-center" style={{ paddingTop: '16px' }}>
          <div style={{
            width: '80px', height: '80px', borderRadius: '9999px',
            backgroundColor: C.secondaryContainer,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '24px',
            boxShadow: '0 8px 24px rgba(85,160,73,0.25)',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: '44px', color: C.onSecondaryContainer, fontVariationSettings: "'FILL' 1" }}>
              check_circle
            </span>
          </div>

          <h2 style={{ fontFamily: 'Space Grotesk', fontSize: '28px', fontWeight: 700, color: C.primary, marginBottom: '8px' }}>
            REGISTRATION COMPLETE
          </h2>
          <p style={{ fontFamily: 'Noto Sans JP', fontSize: '14px', color: C.onSurfaceVariant, marginBottom: '32px' }}>
            {form.orgType === 'organization'
              ? `「${form.orgName}」として登録が完了しました`
              : '個人アカウントが作成されました'}
          </p>

          <div style={{
            width: '100%', padding: '20px 24px', borderRadius: '16px',
            backgroundColor: C.surfaceContainerLow, border: `1px solid ${C.outlineVariant}`,
            marginBottom: '32px', textAlign: 'left',
          }}>
            <p style={{ fontFamily: 'Noto Sans JP', fontSize: '14px', color: C.onSurface, marginBottom: '8px' }}>
              ログインしてドローン管理を始めましょう。
            </p>
            {form.orgType === 'personal' && (
              <p style={{ fontFamily: 'Noto Sans JP', fontSize: '12px', color: C.onSurfaceVariant, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>info</span>
                設定画面からいつでも組織にアップグレードできます
              </p>
            )}
          </div>

          <AuthSubmitButton type="button" onClick={() => navigate('/login')}>
            ログインへ
          </AuthSubmitButton>
        </div>
      </AuthLayout>
    );
  }

  // ================================================================
  // Step3: メール確認コード
  // ================================================================
  if (step === STEPS.VERIFY) {
    return (
      <AuthLayout leftHeading={LEFT_HEADING} leftBody={LEFT_BODY}>
        <StepIndicator currentStep={step} />

        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '9999px',
            backgroundColor: 'rgba(0,104,183,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: '28px', color: C.primaryContainer, fontVariationSettings: "'FILL' 1" }}>
              mark_email_unread
            </span>
          </div>
          <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '20px', fontWeight: 700, color: C.primary, marginBottom: '8px' }}>
            CHECK YOUR EMAIL
          </h3>
          <p style={{ fontFamily: 'Noto Sans JP', fontSize: '13px', color: C.onSurfaceVariant }}>
            <span style={{ fontWeight: 600, color: C.onSurface }}>{form.email}</span>
            <br />に確認コードを送信しました
          </p>
        </div>

        <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <ErrorBanner message={error} />
          <Input
            label="VERIFICATION CODE"
            id="verificationCode"
            type="text"
            value={form.verificationCode}
            onChange={(e) => updateForm('verificationCode', e.target.value)}
            placeholder="000000"
            maxLength={6}
            autoFocus
            icon="pin"
            inputStyle={{
              fontSize: '28px',
              fontFamily: 'Space Grotesk',
              letterSpacing: '0.5em',
              textAlign: 'center',
              paddingLeft: '36px',
            }}
          />
          <AuthSubmitButton loading={isLoading}>確認する</AuthSubmitButton>
        </form>

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <span style={{ fontFamily: 'Noto Sans JP', fontSize: '13px', color: C.onSurfaceVariant }}>
            コードが届かない場合は{' '}
          </span>
          <button
            type="button"
            onClick={handleResendCode}
            style={{ fontFamily: 'Noto Sans JP', fontSize: '13px', fontWeight: 600, color: C.primary, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
          >
            再送信
          </button>
        </div>
      </AuthLayout>
    );
  }

  // ================================================================
  // Step2: 利用形態選択
  // ================================================================
  if (step === STEPS.ORG_TYPE) {
    return (
      <AuthLayout leftHeading={LEFT_HEADING} leftBody={LEFT_BODY}>
        <StepIndicator currentStep={step} />

        <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '20px', fontWeight: 700, color: C.primary, marginBottom: '6px' }}>
          SELECT USAGE TYPE
        </h3>
        <p style={{ fontFamily: 'Noto Sans JP', fontSize: '13px', color: C.onSurfaceVariant, marginBottom: '24px' }}>
          後から変更することもできます
        </p>

        <form onSubmit={handleOrgTypeNext} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <ErrorBanner message={error} />
          <OrgTypeCard
            icon="person" title="個人として使う"
            description="自分だけでドローンを管理します。後から組織にアップグレードできます。"
            selected={form.orgType === 'personal'}
            onClick={() => updateForm('orgType', 'personal')}
          />
          <OrgTypeCard
            icon="corporate_fare" title="組織として使う"
            description="チームでドローンを管理します。メンバーを招待して共同管理できます。"
            selected={form.orgType === 'organization'}
            onClick={() => updateForm('orgType', 'organization')}
          />

          {form.orgType === 'organization' && (
            <div style={{ marginTop: '4px' }}>
              <Input
                label="ORGANIZATION NAME" id="orgName" type="text"
                value={form.orgName}
                onChange={(e) => updateForm('orgName', e.target.value)}
                placeholder="例：株式会社NTT e-Drone Technology"
                icon="business" autoFocus
              />
            </div>
          )}

          <div style={{ marginTop: '8px' }}>
            <AuthSubmitButton loading={isLoading} disabled={!form.orgType}>
              登録する
            </AuthSubmitButton>
          </div>
        </form>

        <div style={{ marginTop: '24px' }}>
          <BackButton onClick={() => window.history.back()} />
        </div>
      </AuthLayout>
    );
  }

  // ================================================================
  // Step1: アカウント情報（デフォルト）
  // ================================================================
  return (
    <AuthLayout leftHeading={LEFT_HEADING} leftBody={LEFT_BODY}>
      <StepIndicator currentStep={step} />

      <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '20px', fontWeight: 700, color: C.primary, marginBottom: '6px' }}>
        CREATE ACCOUNT
      </h3>
      <p style={{ fontFamily: 'Noto Sans JP', fontSize: '13px', color: C.onSurfaceVariant, marginBottom: '24px' }}>
        AIRGROWへようこそ
      </p>

      <form onSubmit={handleAccountNext} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <ErrorBanner message={error} />

        <Input label="氏名" id="name" type="text" value={form.name}
          onChange={(e) => updateForm('name', e.target.value)}
          placeholder="例：岡崎 年弘" icon="person" autoFocus />

        <Input label="メールアドレス" id="email" type="email" value={form.email}
          onChange={(e) => updateForm('email', e.target.value)}
          placeholder="example@airgrow.jp" icon="mail" />

        <Input
          label="パスワード" id="password"
          type={showPassword ? 'text' : 'password'}
          value={form.password}
          onChange={(e) => updateForm('password', e.target.value)}
          placeholder="8文字以上、大小英字・数字含む"
          icon="lock"
          endAdornment={
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              className="transition-colors"
              style={{ color: C.outline, display: 'flex', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                {showPassword ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          }
        />

        <Input label="パスワード確認" id="confirmPassword" type="password" value={form.confirmPassword}
          onChange={(e) => updateForm('confirmPassword', e.target.value)}
          placeholder="パスワード再入力" icon="lock" />

        {/* パスワード要件チェックリスト */}
        {form.password && (
          <div style={{ padding: '16px', borderRadius: '12px', backgroundColor: C.surfaceContainerLow, border: `1px solid ${C.outlineVariant}` }}>
            <p style={{ fontFamily: 'Space Grotesk', fontSize: '10px', fontWeight: 600, letterSpacing: '0.5px', color: C.primary, marginBottom: '10px' }}>
              PASSWORD REQUIREMENTS
            </p>
            {[
              { check: passwordChecks.length,    label: '8文字以上' },
              { check: passwordChecks.uppercase, label: '大文字を含む (A-Z)' },
              { check: passwordChecks.lowercase, label: '小文字を含む (a-z)' },
              { check: passwordChecks.number,    label: '数字を含む (0-9)' },
              { check: passwordChecks.match,     label: 'パスワードが一致' },
            ].map(({ check, label }) => (
              <div key={label} className="flex items-center gap-2" style={{ marginBottom: '6px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px', color: check ? C.onSecondaryContainer : C.outlineVariant, fontVariationSettings: "'FILL' 1" }}>
                  {check ? 'check_circle' : 'radio_button_unchecked'}
                </span>
                <span style={{ fontFamily: 'Noto Sans JP', fontSize: '12px', color: check ? C.onSurface : C.outlineVariant }}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: '4px' }}>
          <AuthSubmitButton disabled={!isAccountFormValid}>次へ</AuthSubmitButton>
        </div>
      </form>

      {/* ログインリンク */}
      <div style={{
        marginTop: '28px', paddingTop: '20px',
        borderTop: `1px solid ${C.surfaceContainerHighest}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <p style={{ fontFamily: 'Noto Sans JP', fontSize: '13px', color: C.onSurfaceVariant }}>
          すでにアカウントをお持ちの方
        </p>
        <Link
          to="/login"
          className="flex items-center gap-0.5"
          style={{ fontFamily: 'Noto Sans JP', fontSize: '13px', fontWeight: 600, color: C.primary, textDecoration: 'none' }}
          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
        >
          ログイン
          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chevron_right</span>
        </Link>
      </div>
    </AuthLayout>
  );
}
